// `@woo/api/extension/extension-user-message-signature.api.ng1.ts` and the composer's other
// reads (plan §7 phase 3, slice 4).
//
// ⚠️ THE WRITES LAND ONE AT A TIME, WITH THE FLOW THAT DRIVES THEM. Send landed with
// `7bb10cd` and the signature save with the composer's SAVE; the draft autosave and template
// create/update are still absent. Nothing here sends anything by being imported.
import { request } from './request.ts'

/** `IExtensionUserMessageSignatureDTO`. */
export interface MessageSignature {
  /** Null for a user who has never set one — the server answers 200 with no value. */
  signature?: string | null
  /**
   * Must be true to save an EMPTY signature; the server rejects an empty save without it
   * (upstream woo@10e2fe0302, HubSpot 49214431098 — a blur autosave of a not-yet-loaded editor
   * wiped a stored 76KB signature). `getByUserId` now answers it too, always `false`.
   *
   * `saveMessageSignature` sends it; `getByUserId` always answers `false`.
   */
  clear?: boolean
}

/**
 * `getByUserId` — the signature block under every message body.
 *
 * ⚠️ A REJECTION IS NOT "NO SIGNATURE", and since woo@10e2fe0302 upstream stopped treating it
 * as one. A user who has never set a signature gets 200 with `signature: null`
 * (`ExtensionUserMessageSignatureController.getByUserId`); a rejection means the read FAILED,
 * and the composer must keep its editor locked and offer RETRY — an empty editor that can be
 * opened is an empty editor that can be saved over the stored signature.
 */
export async function getMessageSignature(): Promise<MessageSignature | undefined> {
  return request('GET', '/chrome-extension-api/extension-user-message-signature/getByUserId')
}

/**
 * `createOrUpdateSignature` — POST, JSON body `{ signature, clear }`, no response body.
 *
 * ⚠️ AN EMPTY SIGNATURE NEEDS `clear: true` OR THE SERVER ANSWERS 400 (woo@10e2fe0302:
 * `saveOrUpdateSignature` throws on an empty save without the flag). That is the server-side
 * half of the fix for a wiped 76KB signature, and the caller must send `signature: null` with
 * it, as upstream's `saveSignature(clear)` does.
 */
export async function saveMessageSignature(signature: MessageSignature): Promise<void> {
  await request('POST', '/chrome-extension-api/extension-user-message-signature/createOrUpdateSignature', signature)
}

/** `IExtensionEntityMessageAttachmentUploadDTO` — an upload already staged server-side. */
export interface MessageAttachment {
  fileName: string
  fileId?: string
}

/** `IExtensionEntityFollowupMessageEmailDataDTO` — a scheduled nudge after the first mail. */
export interface FollowupMessage {
  followupBody: string
  followupDaysUntilSend: number
  attachments?: MessageAttachment[]
}

/** `IExtensionCandidateMessageEmailDataDTO`. `filter` is required — see `sendCandidateMessages`. */
export interface SendCandidateMessagesRequest {
  candidateIds: number[]
  alertId?: number
  emailSubject: string
  emailBody: string
  emailSignature?: string
  attachments?: MessageAttachment[]
  followupDetails?: FollowupMessage[]
  /**
   * ⚠️ REQUIRED, AND THE ENDPOINT NAME SAYS WHY — `filterAndSendMessageToCandidatesByIds`. The
   * server FILTERS the id list before sending (blacklists, missing addresses, already-messaged)
   * and answers with `sentIds` and `blacklistedIds`. Omitting it does not mean "send to all";
   * it means the server applies no filter and mails people the UI had already excluded.
   */
  filter: Record<string, unknown>
  /** Add the same candidates to an ATS job in the same submit — a second outward write. */
  addToAtsJobRequest?: { jobId: number; status: string; candidateIds: number[]; alertId?: number }
}

/** `IExtensionEntityMessageEmailResponseDTO`. */
export interface SendCandidateMessagesResponse {
  sentIds: number[]
  blacklistedIds: number[]
  messageDate?: number
}

/**
 * `filterAndSendMessageToCandidatesByIds` — the message send.
 *
 * ⚠️ THIS PUTS MAIL IN A RECRUITER'S OUTBOX AND IN A CANDIDATE'S INBOX. It is the furthest-
 * reaching write in this client: an ATS record can be deleted, a sent email cannot. Nothing
 * should call it except a user pressing Send.
 *
 * ⚠️ AND A 200 IS NOT "EVERYONE WAS MAILED". The response splits the list into `sentIds` and
 * `blacklistedIds`, and the UI has to report the second — a recruiter who selected twelve and
 * sees no error will assume twelve were contacted.
 */
export async function sendCandidateMessages(
  request_: SendCandidateMessagesRequest,
): Promise<SendCandidateMessagesResponse> {
  return request(
    'POST',
    '/chrome-extension-api/extension-candidate-message/filterAndSendMessageToCandidatesByIds',
    request_,
  )
}

/**
 * `IEmailIntegrationDetails` — whether this user's mailbox is connected, and to what.
 *
 * ⚠️ `emailServerType` IS NULLABLE AND THE CALLER HAS TO TREAT NULL AS ABSENT. Upstream passes
 * it on to the sign-in state as `=== null ? undefined : …`, because a `null` in the query
 * string is the string "null" and the screen then offers a provider that does not exist.
 */
export interface EmailIntegrationDetails {
  emailServerType: string | null
  enabled: boolean
}

/**
 * `getEmailIntegrationDetails` — the gate in front of the composer.
 *
 * ⚠️ IT IS A READ, AND IT IS WHAT DECIDES WHICH SCREEN OPENS. `attemptMessageCandidates`
 * (extension-alert-candidates.component.ng1.ts:911) calls it BEFORE navigating: a user with no
 * connected mailbox goes to `…/email-signin`, everyone else to `…/candidate-messages`. Sending
 * them straight to the composer gives an account that cannot send mail a Send button.
 */
export async function getCandidateEmailIntegrationDetails(): Promise<EmailIntegrationDetails> {
  return request('GET', '/chrome-extension-api/extension-candidate-message/getEmailIntegrationDetails')
}

/** The contacts twin — a different controller, the same shape (`…client-contact-message/…`). */
export async function getClientContactEmailIntegrationDetails(): Promise<EmailIntegrationDetails> {
  return request('GET', '/chrome-extension-api/extension-client-contact-message/getEmailIntegrationDetails')
}

/**
 * `getRemainingDailyMessagesForUser` — how many more people this user may mail today.
 *
 * ⚠️ WITHOUT IT THE COMPOSER CANNOT SEND AT ALL, and that is by design rather than by
 * accident: `canSendMessage` requires `remainingDaily > 0` strictly, and `MessageComposer`
 * defaults it to 0 so an unwired host gets an inert button. A route that wires `onSend` and
 * forgets this one call ships a Send that looks armed in the code and is dead on the screen.
 *
 * The endpoint answers a bare number, which `request` parses as one.
 */
export async function getRemainingDailyMessages(entity: MessageEntity = 'candidates'): Promise<number> {
  return request('GET', `${messageBase(entity)}/getRemainingDailyMessagesForUser`)
}

/** Which product's message controller a call belongs to — the two are separate endpoints. */
export type MessageEntity = 'candidates' | 'clientContacts'

function messageBase(entity: MessageEntity): string {
  return entity === 'clientContacts'
    ? '/chrome-extension-api/extension-client-contact-message'
    : '/chrome-extension-api/extension-candidate-message'
}

/**
 * `IExtensionClientContactMessageEmailDataDTO` — the same envelope as the candidate one with
 * `clientContactIds` in place of `candidateIds`, which is the whole difference
 * (`extension-alert-client-contact-message.api.ng1.ts:26`).
 */
export interface SendClientContactMessagesRequest extends Omit<SendCandidateMessagesRequest, 'candidateIds'> {
  clientContactIds: number[]
}

/**
 * `filterAndSendMessageToClientContactsByIds` — the contacts twin of `sendCandidateMessages`,
 * and every warning on that function applies here too.
 *
 * ⚠️ IT EXISTS SO THE ONE ROUTE CAN SERVE BOTH PRODUCTS. `CandidateMessagesRoute` renders the
 * contacts composer as well (`entity="clientContacts"`), and wiring Send to the candidate
 * endpoint for both would post contact ids to the candidate controller — which answers 200
 * for an empty filter result and mails nobody, the quietest possible wrong.
 */
export async function sendClientContactMessages(
  request_: SendClientContactMessagesRequest,
): Promise<SendCandidateMessagesResponse> {
  return request(
    'POST',
    '/chrome-extension-api/extension-client-contact-message/filterAndSendMessageToClientContactsByIds',
    request_,
  )
}

/**
 * `ExtensionMessageFailureReason` (`extension-alert-candidates-message.api.ng1.ts:46`) — why a
 * message that has no `sendDate` will not get one (upstream woo@9d1ab15f0a).
 */
export type MessageFailureReason = 'ATTACHMENT_MISSING' | 'SEND_FAILED'

/**
 * `IExtensionEntityMessageDataDTO` — one line of a candidate's or contact's Message History.
 *
 * ⚠️ `sendDate` IS NULL FOR A MESSAGE THAT HAS NOT GONE OUT, and that is two states, not one:
 * with no `failureReason` it is still queued ("Pending"), with one it never will be
 * ("Failed: …"). The live endpoint answers `failureReason: null` for a sent message, not an
 * absent key, so both are typed.
 */
export interface EntityMessageData {
  sendDate?: number | null
  creationDate?: number | null
  userName?: string | null
  alertName?: string | null
  failureReason?: MessageFailureReason | null
}

/**
 * `getMessageDataForEntity` — the Message History tooltip behind the slim row's envelope.
 *
 * ⚠️ A GET WITH THE ID IN THE QUERY, AND THE PARAMETER NAME IS PER CONTROLLER: `candidateId`
 * on `getMessageDataForCandidate`, `clientContactId` on `getMessageDataForClientContact`
 * (`extension-alert-client-contact-message.api.ng1.ts:62`). The response is a bare array.
 */
export async function getMessageDataForEntity(entity: MessageEntity, entityId: number): Promise<EntityMessageData[]> {
  const query =
    entity === 'clientContacts'
      ? `getMessageDataForClientContact?${new URLSearchParams({ clientContactId: String(entityId) }).toString()}`
      : `getMessageDataForCandidate?${new URLSearchParams({ candidateId: String(entityId) }).toString()}`
  return request('GET', `${messageBase(entity)}/${query}`)
}
